import { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import BylineFixCard from "@/components/BylineFixCard";
import { useAuth } from "@/contexts/AuthContext";
import { useSiteFlags } from "@/contexts/SiteFlagsContext";
import { toast } from "sonner";
import PageMeta from "@/components/PageMeta";
import NotificationsBell from "@/components/NotificationsBell";
import FamilyPlanPanel from "@/components/FamilyPlanPanel";
import { BillingPanel } from "@/components/BillingPanel";
import VoiceSearchSettings from "@/components/VoiceSearchSettings";
import DietProfileSettings from "@/components/DietProfileSettings";
import {
  User, Envelope, Lock, DownloadSimple, XCircle,
  ArrowCounterClockwise, Trash, Warning, CheckCircle, Gift, UploadSimple,
  Trophy, Fire, Snowflake, IdentificationBadge, ShieldCheck,
} from "@phosphor-icons/react";

export default function Profile() {
  const { user, loading, logout, refresh } = useAuth();
  const { flags, limits } = useSiteFlags();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [usernameCheck, setUsernameCheck] = useState({ ok: null, reason: "" });
  const [usernameSaving, setUsernameSaving] = useState(false);
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [giftCode, setGiftCode] = useState("");
  const [signingOutOthers, setSigningOutOthers] = useState(false);
  const [busy, setBusy] = useState(false);
  const [uploadingPic, setUploadingPic] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const picRef = useRef(null);

  useEffect(() => {
    if (user) {
      setName(user.name || "");
      setEmail(user.email || "");
      setUsername(user.username || "");
    }
  }, [user]);

  // Debounced username availability check — hits the auth endpoint after
  // the user pauses typing so we don't hammer the API on every keystroke.
  useEffect(() => {
    if (!user) return;
    const trimmed = username.trim();
    if (!trimmed) {
      setUsernameCheck({ ok: null, reason: "" });
      return;
    }
    if (trimmed === (user.username || "")) {
      setUsernameCheck({ ok: true, reason: "This is your current username." });
      return;
    }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/me/username-available?u=${encodeURIComponent(trimmed)}`);
        setUsernameCheck(data);
      } catch {
        setUsernameCheck({ ok: false, reason: "Could not check availability." });
      }
    }, 350);
    return () => clearTimeout(t);
  }, [username, user]);

  // Wait for auth check to resolve before showing the "Please sign in" prompt —
  // otherwise the page flashes a fake-logout UI for ~200ms on every fresh load.
  if (loading) {
    return (
      <div className="max-w-md mx-auto p-12 text-center" data-testid="profile-auth-loading">
        <div className="w-8 h-8 border-2 border-rf-orange border-t-transparent rounded-full animate-spin mx-auto" aria-hidden="true" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="max-w-md mx-auto p-12 text-center">
        <p className="text-rf-ink-soft mb-4">Please sign in to view your profile.</p>
        <Link to="/login" className="btn-amber">Sign in</Link>
      </div>
    );
  }

  const saveUsername = async () => {
    const trimmed = username.trim();
    if (!trimmed) {
      toast.error("Pick a username to save.");
      return;
    }
    if (usernameCheck.ok === false) {
      toast.error(usernameCheck.reason || "Username not available.");
      return;
    }
    setUsernameSaving(true);
    try {
      const { data } = await api.patch("/me", { username: trimmed });
      await refresh();
      const cascaded = data?._creator_name_cascaded || 0;
      if (cascaded > 0) {
        toast.success("Public username updated.", {
          description: `Also updated the by-line on ${cascaded} of your existing recipe${cascaded === 1 ? "" : "s"} to match. Custom family attributions were left untouched.`,
          duration: 6000,
        });
      } else {
        toast.success("Public username updated.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save username.");
    } finally {
      setUsernameSaving(false);
    }
  };

  const clearUsername = async () => {
    if (!window.confirm("Clear your public username? You'll show as 'Anonymous Cook' on public pages until you set a new one.")) return;
    setUsernameSaving(true);
    try {
      const { data } = await api.patch("/me", { username: "" });
      setUsername("");
      await refresh();
      const cascaded = data?._creator_name_cascaded || 0;
      if (cascaded > 0) {
        toast.success("Username cleared.", {
          description: `Reverted ${cascaded} recipe by-line${cascaded === 1 ? "" : "s"} to your real name. Custom attributions were left untouched.`,
          duration: 6000,
        });
      } else {
        toast.success("Username cleared.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not clear username.");
    } finally {
      setUsernameSaving(false);
    }
  };

  const saveProfile = async (e) => {
    e?.preventDefault?.();
    setBusy(true);
    try {
      await api.patch("/me", { name, email });
      await refresh();
      toast.success("Profile updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally { setBusy(false); }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pwForm.new_password !== pwForm.confirm) {
      toast.error("Passwords don't match"); return;
    }
    if (pwForm.new_password.length < 6) {
      toast.error("Password must be at least 6 characters"); return;
    }
    setBusy(true);
    try {
      await api.post("/me/change-password", {
        current_password: pwForm.current_password,
        new_password: pwForm.new_password,
      });
      setPwForm({ current_password: "", new_password: "", confirm: "" });
      toast.success("Password updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not change password");
    } finally { setBusy(false); }
  };

  const signOutOtherDevices = async () => {
    if (!window.confirm("Sign out every OTHER browser and device where you're logged in? You'll stay signed in here.")) return;
    setSigningOutOthers(true);
    try {
      const { data } = await api.post("/me/sign-out-others");
      // Swap our locally-stored JWT for the freshly-minted one so THIS
      // session keeps working while every other Bearer token + session
      // cookie for the account is invalidated server-side.
      if (data?.token) {
        localStorage.setItem("rf_token", data.token);
      }
      toast.success("All other devices signed out. You'll stay signed in here.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not sign out other devices");
    } finally { setSigningOutOthers(false); }
  };

  const downloadArchive = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/me/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `recipefiler-archive-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Archive downloaded");
    } catch { toast.error("Export failed"); }
    finally { setBusy(false); }
  };

  const cancelSubscription = async () => {
    if (!window.confirm("Cancel your subscription? Cookbooks stay visible for 1 year, then archived for 1 year before deletion.")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/me/cancel-subscription");
      await refresh();
      toast.success(data.message || "Cancelled");
    } catch { toast.error("Cancellation failed"); }
    finally { setBusy(false); }
  };

  const reactivate = async () => {
    setBusy(true);
    try {
      await api.post("/me/reactivate-subscription");
      await refresh();
      toast.success("Subscription reactivated");
    } catch { toast.error("Reactivation failed"); }
    finally { setBusy(false); }
  };

  const redeemGift = async () => {
    if (!giftCode.trim()) return;
    setBusy(true);
    try {
      await api.post("/billing/redeem-gift", { code: giftCode });
      setGiftCode("");
      await refresh();
      toast.success("Gift code redeemed!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid code");
    } finally { setBusy(false); }
  };

  const deleteAccount = async () => {
    if (confirmText !== "DELETE") { toast.error("Type DELETE to confirm"); return; }
    setBusy(true);
    try {
      await api.delete("/me");
      await logout();
      toast.success("Account deleted");
      nav("/");
    } catch { toast.error("Deletion failed"); setBusy(false); }
  };

  const uploadPicture = async (file) => {
    if (!file) return;
    setUploadingPic(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      await api.patch("/me", { picture: data.url });
      await refresh();
      toast.success("Profile picture updated");
    } catch { toast.error("Upload failed"); }
    finally { setUploadingPic(false); }
  };

  const removePicture = async () => {
    setBusy(true);
    try {
      await api.patch("/me", { picture: null });
      await refresh();
      toast.success("Picture removed");
    } catch { toast.error("Could not remove"); }
    finally { setBusy(false); }
  };

  const archiveStateLabel = {
    active: { tone: "ok", text: "Active subscription" },
    archive_year1: { tone: "warn", text: "Cancelled — cookbooks visible for 1 year" },
    archive_year2: { tone: "warn", text: "Archive year 2 — cookbooks hidden from public" },
    archive_expired: { tone: "danger", text: "Archive expired — content scheduled for removal" },
  }[user.archive_state || "active"];

  const tierLabel = { free: "Taster (Free)", "1mo": "1 month", "3mo": "3 months", "6mo": "6 months", annual: "12 months" }[user.subscription_tier] || user.subscription_tier;

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-10 py-12">
      <PageMeta title="Profile & account" noindex />
      <Link to="/dashboard" className="text-sm font-bold text-rf-orange hover:underline mb-6 inline-block">← Dashboard</Link>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="overline mb-3">Account settings</p>
          <h1 className="font-display text-4xl md:text-6xl font-black text-rf-ink mb-3 leading-[0.95]">Your profile.</h1>
          <p className="text-rf-ink-soft mb-10 max-w-xl">Manage your account, password, subscription and data — all in one place.</p>
        </div>
        <div className="mt-2">
          <NotificationsBell />
        </div>
      </div>

      {/* Avatar */}
      <div className="card-recipe mb-8 flex flex-wrap items-center gap-6">
        <div className="w-24 h-24 rounded-full overflow-hidden bg-rf-amber/20 border-4 border-rf-amber/40 flex items-center justify-center shrink-0">
          {user.picture ? (
            <img src={user.picture} alt={user.name} className="w-full h-full object-cover" />
          ) : (
            <span className="font-display font-black text-3xl text-rf-orange">{(user.name || "?").charAt(0).toUpperCase()}</span>
          )}
        </div>
        <div className="flex-1 min-w-[200px]">
          <p className="overline mb-1">Profile picture</p>
          <p className="font-display font-black text-xl text-rf-ink mb-1">{user.name}</p>
          <p className="text-sm text-rf-ink-soft">Shown next to your shared notes and on your public cookbooks.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button data-testid="profile-pic-upload" onClick={() => picRef.current?.click()} disabled={uploadingPic} className="btn-outline-ink !py-2.5 !px-4 !text-sm">
            <UploadSimple size={16} /> {uploadingPic ? "Uploading…" : (user.picture ? "Replace" : "Upload picture")}
          </button>
          {user.picture && <button onClick={removePicture} className="text-xs font-bold text-red-600 hover:underline">Remove</button>}
          <input ref={picRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => uploadPicture(e.target.files?.[0])} />
        </div>
      </div>

      {/* Subscription banner */}
      <div className={`card-recipe mb-8 ${archiveStateLabel.tone === "warn" ? "!bg-rf-amber/10" : archiveStateLabel.tone === "danger" ? "!bg-red-50" : ""}`}>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="overline mb-1">Subscription</p>
            <p className="font-display text-2xl font-black text-rf-ink">{tierLabel}</p>
            {user.subscription_expires_at && (
              <p className="text-sm text-rf-ink-soft mt-1">Expires {new Date(user.subscription_expires_at).toLocaleDateString()}</p>
            )}
            <p className={`text-xs font-bold mt-2 ${archiveStateLabel.tone === "ok" ? "text-rf-orange" : archiveStateLabel.tone === "danger" ? "text-red-600" : "text-rf-orange"}`}>
              {archiveStateLabel.text}
            </p>
            {user.subscription_cancelled_at && (
              <p className="text-xs text-rf-ink-soft mt-1">Cancelled on {new Date(user.subscription_cancelled_at).toLocaleDateString()}</p>
            )}

            {/* Free-tier usage meter — makes it visually crystal clear that
                the {FREE} limit is on SAVED recipes only. Scans, URL
                previews and drafts stay unlimited. */}
            {user.subscription_tier === "free" && (
              <div className="mt-4 max-w-md" data-testid="profile-usage-meter">
                <div className="flex items-center justify-between text-xs font-bold mb-1.5">
                  <span className="text-rf-ink">Saved recipes</span>
                  <span className="text-rf-ink-soft">{user.recipe_count} / {limits?.free_recipe_limit ?? 10}</span>
                </div>
                <div className="h-2.5 rounded-full bg-rf-cream-alt overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${user.recipe_count >= (limits?.free_recipe_limit ?? 10) ? "bg-red-500" : user.recipe_count >= (limits?.free_recipe_limit ?? 10) - 2 ? "bg-rf-amber" : "bg-rf-orange"}`}
                    style={{ width: `${Math.min(100, (user.recipe_count / (limits?.free_recipe_limit ?? 10)) * 100)}%` }}
                  />
                </div>
                <p className="text-xs text-rf-ink-soft mt-2 leading-relaxed">
                  Only recipes you tap <b>Save</b> on count. Scanning, pasting URLs and previewing drafts are all <b>unlimited</b> and free — nothing counts until it&apos;s saved into one of your cookbooks.
                </p>
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {user.subscription_tier === "free" && (
              <Link to="/pricing" className="btn-amber !py-2.5 !px-4 text-sm">Upgrade</Link>
            )}
            {user.subscription_cancelled_at ? (
              <button data-testid="profile-reactivate" onClick={reactivate} disabled={busy} className="btn-amber !py-2.5 !px-4 text-sm">
                <ArrowCounterClockwise size={16} /> Reactivate
              </button>
            ) : user.subscription_tier !== "free" && (
              <button data-testid="profile-cancel-sub" onClick={cancelSubscription} disabled={busy} className="btn-outline-ink !py-2.5 !px-4 text-sm">
                <XCircle size={16} /> Cancel subscription
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="lg:col-span-2">
          <BillingPanel />
        </div>
        {/* Family Plan — owner management, member info, or an empty prompt */}
        <div className="lg:col-span-2">
          <FamilyPlanPanel />
        </div>
        <div className="lg:col-span-2">
          <DietProfileSettings />
        </div>
        {/* Voice Search — self-service opt-in. Hidden entirely when admin
            has turned the global feature off. Separate from Kitchen View
            cook-mode voice (which is always available). */}
        {flags.voice_search_enabled && (
          <div className="lg:col-span-2">
            <VoiceSearchSettings />
          </div>
        )}
        {/* Public cook identity — username + gamification opt-in */}
        {(
          <div className="card-recipe lg:col-span-2" data-testid="profile-username-card">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-rf-amber/20 flex items-center justify-center"><IdentificationBadge size={20} weight="duotone" className="text-rf-orange" /></div>
              <h2 className="font-display text-2xl font-black text-rf-ink">Public cook name</h2>
            </div>
            {user.username_suspended ? (
              <div className="rounded-xl bg-red-50 border border-red-200 p-4 text-sm text-red-900" data-testid="profile-username-suspended">
                <b>Your username has been suspended by an admin.</b> Contact <a href="mailto:contactus@recipefiler.com" className="underline">contactus@recipefiler.com</a> to appeal or pick a new one.
              </div>
            ) : (
              <>
                <p className="text-sm text-rf-ink-soft mb-4 leading-relaxed">
                  Your username is the name shown on your recipes (&ldquo;by …&rdquo;), your public <b>cook profile</b> and the <b>Leaderboard</b>. If you don&apos;t set one, your Name is used. Changing it updates your recipes automatically. 3–20 characters, letters / numbers / underscore only.
                </p>
                <div className="flex flex-col sm:flex-row gap-2 items-start">
                  <div className="flex-1 w-full">
                    <div className="flex items-center gap-2">
                      <span className="text-rf-ink-soft font-mono">@</span>
                      <input
                        data-testid="profile-username-input"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        placeholder="your_username"
                        className="input-rf font-mono"
                        maxLength={20}
                      />
                    </div>
                    {username && usernameCheck.reason && (
                      <p data-testid="profile-username-status" className={`text-xs font-bold mt-2 ${usernameCheck.ok ? "text-emerald-700" : "text-red-600"}`}>
                        {usernameCheck.ok ? "✓ " : "✗ "}{usernameCheck.reason}
                      </p>
                    )}
                  </div>
                  <button
                    data-testid="profile-username-save"
                    onClick={saveUsername}
                    disabled={usernameSaving || usernameCheck.ok === false}
                    className="btn-amber whitespace-nowrap"
                  >
                    {usernameSaving ? "Saving…" : "Save username"}
                  </button>
                  {user.username && (
                    <button
                      data-testid="profile-username-clear"
                      onClick={clearUsername}
                      disabled={usernameSaving}
                      className="btn-outline-ink whitespace-nowrap"
                    >
                      Clear
                    </button>
                  )}
                </div>

                {/* Streak / level snapshot */}
                {(flags.gamification_enabled || ["Admin", "SuperAdmin"].includes(user?.role)) && <div className="mt-6 grid grid-cols-3 gap-4">
                  <div className="rounded-xl bg-rf-cream-alt p-3 text-center">
                    <Fire size={18} weight={user.current_streak > 0 ? "fill" : "regular"} className="mx-auto text-rf-orange mb-1" />
                    <p className="font-display text-2xl font-black text-rf-ink leading-none">{user.current_streak || 0}</p>
                    <p className="text-[10px] uppercase tracking-wider text-rf-ink-soft font-bold mt-1">Current streak</p>
                  </div>
                  <div className="rounded-xl bg-rf-cream-alt p-3 text-center">
                    <Trophy size={18} weight="fill" className="mx-auto text-rf-amber mb-1" />
                    <p className="font-display text-2xl font-black text-rf-ink leading-none">{user.longest_streak || 0}</p>
                    <p className="text-[10px] uppercase tracking-wider text-rf-ink-soft font-bold mt-1">Longest streak</p>
                  </div>
                  <div className="rounded-xl bg-rf-cream-alt p-3 text-center">
                    <Snowflake size={18} weight="fill" className="mx-auto text-sky-500 mb-1" />
                    <p className="font-display text-2xl font-black text-rf-ink leading-none">{user.streak_freezes || 0}</p>
                    <p className="text-[10px] uppercase tracking-wider text-rf-ink-soft font-bold mt-1">Freezes</p>
                  </div>
                </div>}
                {user.username && (
                  <Link to={`/cook/${user.user_id}`} className="mt-4 inline-block text-sm font-bold text-rf-orange hover:underline" data-testid="profile-view-public-card">
                    View your public cook profile →
                  </Link>
                )}
              </>
            )}
          </div>
        )}

        <BylineFixCard refreshKey={`${user.username || ""}|${user.name || ""}`} />

        {/* Account details */}
        <form onSubmit={saveProfile} className="card-recipe">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-full bg-rf-amber/20 flex items-center justify-center"><User size={20} weight="duotone" className="text-rf-orange" /></div>
            <h2 className="font-display text-2xl font-black text-rf-ink">Account details</h2>
          </div>
          <div className="space-y-4">
            <div>
              <label className="overline" htmlFor="profile-name">Name</label>
              <input id="profile-name" data-testid="profile-name" value={name} onChange={(e) => setName(e.target.value)} className="input-rf mt-1" />
            </div>
            <div>
              <label className="overline" htmlFor="profile-email">Email</label>
              <input id="profile-email" data-testid="profile-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="input-rf mt-1" />
            </div>
            <button data-testid="profile-save" disabled={busy} className="btn-amber w-full">Save changes</button>
          </div>
        </form>

        {/* Change password */}
        <form onSubmit={changePassword} className="card-recipe">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-full bg-rf-amber/20 flex items-center justify-center"><Lock size={20} weight="duotone" className="text-rf-orange" /></div>
            <h2 className="font-display text-2xl font-black text-rf-ink">Change password</h2>
          </div>
          <div className="space-y-4">
            <div>
              <label className="overline" htmlFor="profile-pw-current">Current password</label>
              <input id="profile-pw-current" data-testid="profile-pw-current" type="password" autoComplete="current-password" value={pwForm.current_password} onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })} className="input-rf mt-1" />
            </div>
            <div>
              <label className="overline" htmlFor="profile-pw-new">New password</label>
              <input id="profile-pw-new" data-testid="profile-pw-new" type="password" autoComplete="new-password" minLength={6} value={pwForm.new_password} onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })} className="input-rf mt-1" />
            </div>
            <div>
              <label className="overline" htmlFor="profile-pw-confirm">Confirm new password</label>
              <input id="profile-pw-confirm" data-testid="profile-pw-confirm" type="password" autoComplete="new-password" minLength={6} value={pwForm.confirm} onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })} className="input-rf mt-1" />
            </div>
            <button data-testid="profile-pw-save" disabled={busy} className="btn-amber w-full">Update password</button>
          </div>
        </form>

        {/* Sign out other devices */}
        <div className="card-recipe">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-rf-amber/20 flex items-center justify-center"><ShieldCheck size={20} weight="duotone" className="text-rf-orange" /></div>
            <h2 className="font-display text-2xl font-black text-rf-ink">Sign out other devices</h2>
          </div>
          <p className="text-rf-ink-soft text-sm mb-4">
            Think you left yourself signed in on a friend&apos;s phone or a shared laptop? One tap kicks every other browser and device out of your account. You&apos;ll stay signed in here.
          </p>
          <button
            data-testid="profile-sign-out-others"
            onClick={signOutOtherDevices}
            disabled={signingOutOthers}
            className="btn-outline-ink w-full inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <ShieldCheck size={18} weight="bold" /> {signingOutOthers ? "Signing out others…" : "Sign out all other devices"}
          </button>
        </div>

        {/* Redeem gift */}
        <div className="card-recipe">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-full bg-rf-amber/20 flex items-center justify-center"><Gift size={20} weight="duotone" className="text-rf-orange" /></div>
            <h2 className="font-display text-2xl font-black text-rf-ink">Redeem gift code</h2>
          </div>
          <div className="flex gap-2">
            <input data-testid="profile-gift-code" value={giftCode} onChange={(e) => setGiftCode(e.target.value.toUpperCase())} placeholder="10-CHAR CODE" className="input-rf font-mono uppercase tracking-wider" />
            <button data-testid="profile-gift-redeem" onClick={redeemGift} disabled={busy} className="btn-amber !rounded-xl !px-6">Redeem</button>
          </div>
          <p className="text-xs text-rf-ink-soft mt-3">Gift codes are 10 alpha-numeric characters and unlock the 12-month plan.</p>
        </div>

        {/* Data export */}
        <div className="card-recipe">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-full bg-rf-amber/20 flex items-center justify-center"><DownloadSimple size={20} weight="duotone" className="text-rf-orange" /></div>
            <h2 className="font-display text-2xl font-black text-rf-ink">Your data</h2>
          </div>
          <p className="text-rf-ink-soft text-sm mb-4">Download a complete archive of your recipes, cookbooks and account in JSON format (GDPR-compliant).</p>
          <button data-testid="profile-export" onClick={downloadArchive} disabled={busy} className="btn-outline-ink w-full">
            <DownloadSimple size={18} /> Download My Recipe Filer Archive
          </button>
        </div>
      </div>

      {/* Danger zone */}
      <div className="mt-12 card-recipe !border-red-200 !bg-red-50/50">
        <div className="flex items-center gap-3 mb-3">
          <Warning size={24} weight="duotone" className="text-red-600" />
          <h2 className="font-display text-2xl font-black text-red-900">Danger zone</h2>
        </div>
        <p className="text-red-900/70 text-sm mb-5">Deleting your account is permanent. All recipes, cookbooks and uploads are erased within 30 days under GDPR right-to-erasure rules.</p>
        {!showDelete ? (
          <button data-testid="profile-delete-toggle" onClick={() => setShowDelete(true)} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-bold bg-red-600 text-white hover:bg-red-700 transition-colors">
            <Trash size={18} /> Delete my account
          </button>
        ) : (
          <div className="space-y-3">
            <p className="text-red-900 text-sm font-semibold">Type DELETE to confirm:</p>
            <input data-testid="profile-delete-confirm" value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="DELETE" className="input-rf font-mono uppercase tracking-wider !border-red-300" />
            <div className="flex gap-3">
              <button onClick={() => { setShowDelete(false); setConfirmText(""); }} className="btn-outline-ink">Cancel</button>
              <button data-testid="profile-delete-final" onClick={deleteAccount} disabled={busy || confirmText !== "DELETE"} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-bold bg-red-600 text-white hover:bg-red-700 disabled:opacity-40 transition-colors">
                <Trash size={18} /> Permanently delete
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
